'use client';

import { trackEvent } from '@vm/analytics/client';

/**
 * SEO Analytics Events
 *
 * Track user engagement with SEO landing pages to measure:
 * - Which pages drive traffic
 * - Which CTAs convert
 * - Attribution for signups from SEO
 */

export type SEOIntentType =
  | 'track-subscriptions'
  | 'cancel-subscriptions'
  | 'forgotten-subscriptions'
  | 'subscription-tracker-for-adhd'
  | 'manage-recurring-payments'
  | 'subscription-tracking'; // hub page

export interface SEOPageViewParams {
  page_slug: string;
  intent_type: SEOIntentType;
}

export interface SEOCTAClickParams {
  page_slug: string;
  cta_variant: string;
  cta_location: 'hero' | 'bottom' | 'inline';
}

export interface SEOSignupParams {
  page_slug: string;
}

/**
 * Track when a user views an SEO landing page
 */
export function trackSEOPageView(params: SEOPageViewParams) {
  trackEvent('seo_page_viewed', {
    page_slug: params.page_slug,
    intent_type: params.intent_type,
  });
}

/**
 * Track when a user clicks a CTA on an SEO page
 */
export function trackSEOCTAClick(params: SEOCTAClickParams) {
  trackEvent('seo_cta_clicked', {
    page_slug: params.page_slug,
    cta_variant: params.cta_variant,
    cta_location: params.cta_location,
  });
}

/**
 * Track when a user completes signup from an SEO page
 * Call this after successful authentication
 */
export function trackSEOSignupCompleted(params: SEOSignupParams) {
  trackEvent('signup_completed_from_seo', {
    page_slug: params.page_slug,
  });
}

/**
 * Store the referrer page slug for attribution after auth redirect
 */
export function setSEOReferrer(pageSlug: string) {
  if (typeof window !== 'undefined') {
    sessionStorage.setItem('seo_referrer', pageSlug);
  }
}

/**
 * Get and clear the stored SEO referrer
 */
export function getSEOReferrer(): string | null {
  if (typeof window !== 'undefined') {
    const referrer = sessionStorage.getItem('seo_referrer');
    if (referrer) {
      sessionStorage.removeItem('seo_referrer');
    }
    return referrer;
  }
  return null;
}
